import { Injectable, inject } from '@angular/core';
import { signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { UserService } from '_services/user.service';
import { jwtDecode } from 'jwt-decode';
import { environment } from '../../environments/environment';

/**
 * Mock ECL scenarios (dev/test only). We fabricate the College Board SDK's
 * data — a `cb.core.iam` stub — and let the real setUserName / JWT-decode
 * path run untouched, rather than faking the service's conclusions.
 */
export type EclMockScenario = 'student' | 'professional' | 'studentNoPid';

export const ECL_MOCK_SCENARIOS: ReadonlyArray<{
  id: EclMockScenario;
  label: string;
  description: string;
}> = [
  { id: 'student', label: 'Student', description: 'Consumer login with a cb.pid — full ECL login.' },
  { id: 'professional', label: 'Professional', description: 'Namespace pf — must NOT count as ECL login.' },
  { id: 'studentNoPid', label: 'Student (no PID)', description: 'Valid student, no JWT — logs in on cbUserId alone.' },
];

/** Unsigned static JWT carrying a cb.pid. jwtDecode does not verify signatures. */
function buildMockJwt(pid: string): string {
  const b64url = (o: object) =>
    btoa(JSON.stringify(o)).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');
  return `${b64url({ alg: 'none', typ: 'JWT' })}.${b64url({ cb: { pid } })}.`;
}

@Injectable({
  providedIn: 'root'
})
export class EclService {
  private us = inject(UserService);

  appId = '250';

  private _loggedIn = signal(false);
  readonly loggedIn = this._loggedIn.asReadonly();
  cbUserId = new BehaviorSubject('guest');
  newEclFound = new BehaviorSubject(false);

  initiateEcl() {
    // Admin preview does not use student ECL.
    if (this.us.isAdmin) {
      this.newEclFound.next(false);
      return;
    }

    // Athena widgets (`cb`) ship only in production builds via index.prod.html.
    // Non-prod relies on mock login (`allowMockEclLogin`) instead.
    if (!environment.production) {
      this.newEclFound.next(false);
      return;
    }

    try {
      // Configuration is already set up in index.prod.html before the script loads.
      if (!cb?.core?.utils?.DeploymentProfile) {
        cb.core = cb.core || {};
        cb.core.utils = cb.core.utils || {};
        cb.core.utils.DeploymentProfile = {
          apiKey: '',
          iam: {
            aws: {
              cbAWSDomains: []
            },
            requiresLogin: false,
            loginURL: 'https://account.collegeboard.org/login/login?appId=' + this.appId + '&DURL='
          },
          dataProtector: {
            // Match cb-init.js: never re-arm the protector if we somehow init late in admin.
            protectionEnabled: !this.us.isAdmin
          }
        };
      }

      const iam = cb.core?.iam;
      if (!iam) {
        this.newEclFound.next(false);
        return;
      }

      const iamBus = iam.getEventBus();
      iamBus.on(iam.events.AWSLogin, () => { this.onLogin(); });
      iamBus.on(iam.events.AWSLogout, () => { this.onLogout(); });

      this.setUserName();
      this.newEclFound.next(true);
    } catch (err) {
      console.warn('College Board login could not be initialized.', err);
      this.newEclFound.next(false);
    }
  }

  onLogin() {
    console.log('ECL Logged in');
    this.setUserName();
    console.log(this.cbUserId.value);
  }

  onLogout() {
    console.log('ECL Logged out');
    this._loggedIn.set(false);
    this.cbUserId.next('');
    this.us.cbPersonId = '';
  }

  /**
   * Install a mock College Board session (dev/test only) and run the real
   * login path against it. No real CB round-trip. Gated on `allowMockEclLogin`.
   */
  installMockScenario(scenario: EclMockScenario) {
    if (this.us.isAdmin) {
      return;
    }
    if (!environment.allowMockEclLogin) {
      console.warn('Mock ECL login is disabled for this environment');
      return;
    }
    this.installMockIam(scenario);
    void this.setUserName();
  }

  /** Tear down the mock session and clear login state. */
  mockLogout() {
    const g = globalThis as { cb?: CbGlobal };
    if (g.cb?.core?.iam) {
      delete g.cb.core.iam;
    }
    this.clearEclLoginState();
  }

  private installMockIam(scenario: EclMockScenario) {
    const baseProfile: CbAuthSessionBasicProfile = {
      userName: 'mock-student-uid',
      firstName: 'Mock',
      lastName: 'Student',
      isStudent: true,
      namespace: 'cb',
    };

    let session: CbAuthSession;
    let jwt: string | undefined;
    switch (scenario) {
      case 'professional':
        session = {
          isLoggedIn: true,
          basicProfile: { ...baseProfile, userName: 'mock-pro-uid', isProfessional: true, isStudent: false, namespace: 'pf' },
        };
        jwt = buildMockJwt('mock-pro-pid');
        break;
      case 'studentNoPid':
        session = { isLoggedIn: true, basicProfile: baseProfile };
        jwt = undefined; // getJWTToken() → undefined; login rides on cbUserId alone
        break;
      case 'student':
      default:
        session = { isLoggedIn: true, basicProfile: baseProfile };
        jwt = buildMockJwt('mock-person-id');
        break;
    }

    const mockIam: CbIam = {
      getEventBus: () => ({ on: () => undefined }),
      events: { AWSLogin: 'AWSLogin', AWSLogout: 'AWSLogout', Logout: 'Logout', AWSLoginFailed: 'AWSLoginFailed' },
      getAuthSession: () => session,
      getJWTToken: () => jwt,
      getAuthorizationToken: () => jwt,
    };

    const g = globalThis as { cb?: CbGlobal };
    g.cb = g.cb || {};
    g.cb.core = g.cb.core || {};
    g.cb.core.iam = mockIam;
  }

  async setUserName() {
    const authSession = cb.core?.iam?.getAuthSession();

    // College Board IAM cookies are shared across apps. An EPL (professional)
    // admin session must not count as ECL (student) login for the NPC.
    if (!this.isStudentEclSession(authSession)) {
      this.clearEclLoginState();
      return;
    }

    this._loggedIn.set(true);
    if (authSession.basicProfile?.userName) {
      this.cbUserId.next(authSession.basicProfile.userName);
      this.us.cbUserId = this.cbUserId.value;
    }

    try {
      const JwtToken = cb?.core?.iam?.getJWTToken();
      if (JwtToken) {
        const decodedToken = jwtDecode<{ cb?: { pid?: string } }>(JwtToken);
        const cbPersonId = decodedToken?.cb?.pid;
        if (cbPersonId) {
          this.us.cbPersonId = cbPersonId;
        }
      }
    } catch (error) {
      console.error('Error getting authorization token', error);
      this.us.cbUserId = 'guest';
    }
  }

  /** True only for consumer/student ECL sessions — not professional EPL. */
  private isStudentEclSession(authSession: CbAuthSession | null | undefined): authSession is CbAuthSession {
    if (!authSession?.isLoggedIn) {
      return false;
    }

    const profile = authSession.basicProfile;
    if (!profile) {
      return false;
    }

    if (profile.isProfessional || profile.namespace === 'pf') {
      return false;
    }

    // Prefer an explicit student flag when the SDK provides it.
    if (profile.isStudent === false) {
      return false;
    }

    return true;
  }

  private clearEclLoginState(): void {
    this._loggedIn.set(false);
    this.cbUserId.next('guest');
    this.us.cbUserId = 'guest';
    this.us.cbPersonId = '';
  }

  getDestinationUrl(): string {
    console.log(window.location.protocol, window.location.host, window.location.pathname);
    // return '?appId=' + this.appId + '&DURL=' + window.location.protocol + '//' + window.location.host + window.location.pathname;
    return '?appId=' + this.appId + '&DURL=' + window.location.protocol + '//' + window.location.host + '/app/' + this.us.schoolId;
  }

  getLoginUrl(): string {
    return 'https://account.collegeboard.org/login/login' + this.getDestinationUrl();
  }

  /**
   * College Board login URL that returns to an arbitrary same-origin DURL.
   * The DURL is URL-encoded so its own query string (e.g. ?resumeRoute=...)
   * survives as part of DURL rather than being parsed onto the login URL.
   */
  getLoginUrlForDurl(durl: string): string {
    return 'https://account.collegeboard.org/login/login?appId=' + this.appId + '&DURL=' + encodeURIComponent(durl);
  }
}
